import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save } from "lucide-react";
import { translations } from "../../i18n/translations";
import { getPath } from "../../i18n/LanguageContext";
import { IMAGES } from "../../constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { title: "Startseite", fields: [
    ["hero.eyebrow", "Hero · Oberzeile"], ["hero.title_line1", "Hero · Titel Zeile 1"], ["hero.title_line2", "Hero · Titel Zeile 2"],
    ["hero.subtitle", "Hero · Untertitel", true], ["intro.heading", "Intro · Überschrift"], ["intro.body", "Intro · Text", true],
    ["services_teaser.heading", "Leistungen-Teaser · Überschrift"], ["services_teaser.watches_desc", "Teaser · Luxusuhren", true],
    ["services_teaser.goldsmith_desc", "Teaser · Goldschmiede", true],
  ] },
  { title: "Über uns", fields: [
    ["about.heading", "Überschrift"], ["about.p1", "Absatz 1", true], ["about.p2", "Absatz 2", true],
    ["about.services_list", "Dienstleistungen (eine pro Zeile)", true],
  ] },
  { title: "Leistungen", fields: [["services.heading", "Überschrift"]] },
  { title: "Kontakt", fields: [
    ["contact.heading", "Überschrift"], ["contact.subtitle", "Untertitel", true],
    ["contact.address_lines", "Adresse (eine Zeile pro Zeile)", true], ["contact.hours_lines", "Öffnungszeiten (eine pro Zeile)", true],
    ["contact.whatsapp_note", "WhatsApp-Hinweis", true],
  ] },
];

const IMAGE_FIELDS = [
  ["hero", "Startseite · Hero-Bild"], ["watch_service", "Luxusuhren-Bild"], ["goldsmith", "Goldschmiede-Bild"],
  ["atelier", "Über uns · Atelier-Bild"], ["zurich", "Kontakt · Bild"],
];

const inputCls = "w-full border border-black/15 focus:border-black px-3 py-2 font-sans-aw text-sm outline-none bg-white";
const labelCls = "font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 block mb-1.5";

const toText = (v) => (Array.isArray(v) ? v.join("\n") : v ?? "");
const testId = (s) => s.replace(/[._]/g, "-");

const TextInput = ({ id, value, onChange, multiline }) =>
  multiline ? (
    <textarea data-testid={id} rows={4} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
  ) : (
    <input data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
  );

const buildForm = (settings) => {
  const form = { de: {}, en: {}, images: {} };
  SECTIONS.flatMap((s) => s.fields).forEach(([path]) => {
    ["de", "en"].forEach((l) => {
      form[l][path] = toText(settings.texts?.[l]?.[path] ?? getPath(translations[l], path));
    });
  });
  IMAGE_FIELDS.forEach(([k]) => (form.images[k] = settings.images?.[k] || IMAGES[k]));
  return form;
};

const toPayload = (form) => {
  const texts = { de: {}, en: {} };
  ["de", "en"].forEach((l) => {
    Object.entries(form[l]).forEach(([path, val]) => {
      const def = getPath(translations[l], path);
      const value = Array.isArray(def) ? val.split("\n").map((s) => s.trim()).filter(Boolean) : val;
      const empty = Array.isArray(value) ? !value.length : !value.trim();
      if (!empty && JSON.stringify(value) !== JSON.stringify(def)) texts[l][path] = value;
    });
  });
  const images = Object.fromEntries(Object.entries(form.images).filter(([k, v]) => v && v !== IMAGES[k]));
  return { texts, images };
};

export const SiteContentEditor = ({ token }) => {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    axios.get(`${API}/site/settings`).then((r) => setForm(buildForm(r.data)));
  }, []);

  if (!form) return <p className="font-sans-aw text-sm text-black/50">Lädt…</p>;

  const set = (group, key, value) => setForm({ ...form, [group]: { ...form[group], [key]: value } });

  const save = async () => {
    setStatus("saving");
    try {
      await axios.put(`${API}/admin/site/settings`, toPayload(form), { headers: { "X-Admin-Token": token } });
      setStatus("saved");
    } catch (e) {
      setStatus("error");
    }
  };

  return (
    <div data-testid="site-content-editor">
      {SECTIONS.map((section) => (
        <section key={section.title} className="mb-12">
          <h2 className="font-serif-aw text-2xl mb-6 pb-3 border-b border-black/10">{section.title}</h2>
          <div className="space-y-6">
            {section.fields.map(([path, label, multiline]) => (
              <div key={path}>
                <span className={labelCls}>{label}</span>
                <div className="grid md:grid-cols-2 gap-3">
                  {["de", "en"].map((l) => (
                    <div key={l}>
                      <span className="font-sans-aw text-[9px] tracking-luxury uppercase text-black/40">{l}</span>
                      <TextInput id={`site-field-${testId(path)}-${l}`} value={form[l][path]} onChange={(v) => set(l, path, v)} multiline={multiline} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="mb-12">
        <h2 className="font-serif-aw text-2xl mb-6 pb-3 border-b border-black/10">Bilder</h2>
        <div className="space-y-6">
          {IMAGE_FIELDS.map(([key, label]) => (
            <div key={key} className="flex gap-4 items-end">
              <img src={form.images[key]} alt="" className="w-20 h-20 object-cover border border-black/10" />
              <div className="flex-1">
                <span className={labelCls}>{label} (URL)</span>
                <TextInput id={`site-image-${testId(key)}`} value={form.images[key]} onChange={(v) => set("images", key, v)} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="sticky bottom-0 bg-white border-t border-black/10 py-4 flex items-center gap-4">
        <button data-testid="site-content-save-btn" onClick={save} disabled={status === "saving"} className="font-sans-aw text-[10px] tracking-luxury uppercase bg-black text-white px-5 py-3 inline-flex items-center gap-1.5">
          <Save size={13} /> Website-Texte speichern
        </button>
        <span data-testid="site-content-status" className="font-sans-aw text-sm text-black/60">
          {status === "saved" && "Gespeichert – Änderungen sind live."}
          {status === "error" && "Speichern fehlgeschlagen."}
          {status === "saving" && "Speichert…"}
        </span>
      </div>
    </div>
  );
};
